import type { RoomID } from '@/types/app'
import { useDebounceFn, useLocalStorage } from '@vueuse/core'
import { captureMessage } from '@sentry/vue'
import { computed, ref, watch } from 'vue'
import fastDeepEqual from 'fast-deep-equal'
import type { Ref } from 'vue'
import * as v from 'valibot'

const STORAGE_KEY = '__USE_PERSISTENT_MESSAGE_FORM'
const SYNC_DEBOUNCE_TIME = 100
const SYNC_MAX_WAIT = 1000

const formSchema = v.tuple([
  v.string(), // message
  v.array(v.string()), // selections
])
type Form = v.InferOutput<typeof formSchema>
const emptyForm: Form = ['', []]

export const usePersistentMessageForm = (roomIDRef: Ref<Maybe<RoomID>>) => {
  const stored = useLocalStorage<Record<RoomID, Form>>(STORAGE_KEY, {})

  const getCurrentForm = (roomID: Maybe<RoomID>) => {
    if (roomID == null) {
      return emptyForm
    }

    const input = stored.value[roomID]
    const result = v.safeParse(formSchema, input)
    if (!result.success) {
      // https://www.notion.so/400f/selections-9cc085d957194427a14af263d26066f3?pvs=4
      // localStorage に Form 以外のデータが入っていそうだが、具体的にどのように発生しているかわからないので、検知できるようにログを仕込む
      captureMessage(
        JSON.stringify({
          input,
          isuses: result.issues,
        })
      )
      return emptyForm
    }

    return result.output
  }

  const formRef = ref<Form>(getCurrentForm(roomIDRef.value))

  watch(roomIDRef, roomID => {
    formRef.value = getCurrentForm(roomID)
  })

  watch(formRef, value => {
    debouncedSave(value)
  })

  const debouncedSave = useDebounceFn(
    (form: Form) => {
      if (roomIDRef.value == null) {
        return
      }

      const isFormEmpty = fastDeepEqual(form, emptyForm)
      if (isFormEmpty) {
        // 特定のキーを取り除くための _
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [roomIDRef.value]: _, ...rest } = stored.value
        stored.value = { ...rest }
        return
      }

      stored.value[roomIDRef.value] = form
    },
    SYNC_DEBOUNCE_TIME,
    { maxWait: SYNC_MAX_WAIT }
  )

  const message = computed<string>({
    get: () => formRef.value[0],
    set: value => {
      formRef.value = [value, formRef.value[1]]
    },
  })

  const selections = computed<string[]>({
    get: () => formRef.value[1],
    set: value => {
      formRef.value = [formRef.value[0], value]
    },
  })

  return {
    message,
    selections,
  }
}
